import React, { useEffect, useState } from 'react';
import { Dialog, Pagination } from '@mui/material';
import { Link } from 'react-router-dom';
import { useAuthToken } from '../../hooks/useAuthToken';
import { useCourseTaker } from '../../hooks/useCourseTaker';
import { useFetch } from '../../hooks/useFetch';
import { useUserPlan } from '../../hooks/useUserPlan';
import { Toast } from '../controls/UIControls';
import { arrayPaginated, MINIMUM_CERT_SCORE, PAGE_SIZE, TakeCourseIndicators } from './shared';
import moment from 'moment';
/**
 * Creado por Equilibrio Agency 2022<br>
 * Función compartir certificado
 * @param {boolean} enabled Variable para habilitar botones
 * @param {string} link Enlace a
 * @param {string} icon Ícono del red social a compartir
 * @function ShareButton
 */
const ShareButton = ({ enabled, link, icon }) => {
  const iconButton = (
    <i
      className={`fa-brands fa-${icon} fa-2xl  ProfilePage__share-buttons ProfilePage__share-buttons--${icon} ${enabled ? '' : 'ProfilePage__share-buttons--disabled'}`}
    ></i>
  );

  return enabled ? (
    <a href={link} target="_blank" rel="noreferrer">
      {iconButton}
    </a>
  ) : (
    iconButton
  );
};
/**
 * Creado por Equilibrio Agency 2022<br>
 * Función para descargar certificado
 * @param {string} course Datos del curso
 * @param {string} buttonLabel Nombre del nombre a mostrar
 * @param {event} actionHandler Ícono del red social a compartir
 * @param {boolean} waiting Variable para validación de carga de
 * @param {boolean} disabled Variable para inhabilitar botón de curso
 * @function CourseActionButton
 */
const CourseActionButton = ({ course, buttonLabel, actionHandler, waiting, disabled }) => {
  if (waiting) disabled = true;

  const clickHandler = waiting || disabled ? null : () => actionHandler(course);

  return (
    <div
      className={`col-12 ProfilePage__download-certificate-button ${disabled ? 'ProfilePage__download-certificate-button--disabled' : ''}`}
      onClick={clickHandler}
    >
      <span>{buttonLabel}</span>
      {waiting && (
        <>
          &nbsp;
          <span
            className="spinner-border spinner-border-sm"
            role="status"
            aria-hidden="true"
          ></span>
        </>
      )}
    </div>
  );
};
/**
 * Creado por Equilibrio Agency 2022<br>
 * Función para copiar enlace de certificado a portapapeles
 * @param {string} link Datos del curso
 * @param {string} course Datos del curso
 * @function CopyLinkToClipboard
 */
const CopyLinkToClipboard = ({ link, course }) => {
  const enabled = !!link;
  const [showCopyToast, setShowCopyToast] = useState({ show: false, message: '' });
  const toastReset = () => {
    setShowCopyToast({ show: false });
  };

  const copyTextToClipboard = (text) => {
    if ('clipboard' in navigator) {
      return navigator.clipboard.writeText(text);
    } else {
      return new Promise((resolve, reject) =>
        document.execCommand('copy', true, text) ? resolve() : reject()
      );
    }
  };
  // Función copiar link
  const copyLink = (text, course) => {
    copyTextToClipboard(text)
      .then(() =>
        setShowCopyToast({
          show: true,
          message: `<span>El enlace al certificado del curso <strong>${course}</strong> fue copiado exitosamente.</span>`,
          type: 'success',
        })
      )
      .catch(() =>
        setShowCopyToast({
          show: true,
          message: 'Ocurrió un error al copiar el enlace.',
          type: 'error',
        })
      );
  };

  return (
    <>
      <div
        className={`col-12 ProfilePage__copy-link-button ${enabled ? '' : 'ProfilePage__copy-link-button--disabled'}`}
        onClick={enabled ? () => copyLink(link, course?.name) : null}
      >
        <i className="fa-solid fa-copy"></i>
        <span className="ms-2">Copiar enlace</span>
      </div>
      <Toast
        show={showCopyToast.show}
        message={showCopyToast.message}
        type={showCopyToast.type}
        closeCallback={toastReset}
      />
    </>
  );
};

// Función finalizado curso
export const FinishedCourses = ({ myCourses }) => {
  const coursesFiltered = myCourses.filter(
    (e) => e['user_study.progress'] === 100 && e['user_study.avgScore'] >= MINIMUM_CERT_SCORE
  );
  const numberOfPages = Math.ceil(coursesFiltered.length / PAGE_SIZE);
  const [, handleFetch, handleFetchPDF] = useFetch();
  const { authToken, getSessionUser } = useAuthToken();
  const { checkUserPlan } = useUserPlan();
  const currUser = getSessionUser();
  const [coursesDisplayed, setCoursesDisplayed] = useState(arrayPaginated(coursesFiltered, 1));
  const [showToast, setShowToast] = useState({ show: false, message: '' });
  const [errorDialog, setErrorDialog] = useState(false);
  const [data, courseTaker] = useCourseTaker();
  const [urlCertificate, setUrlCertificate] = useState('');
  const onCourseRedirection = (course) => {
    courseTaker(course, () => setErrorDialog(true));
  };
  // Función para traducir mes a español
  const getSpanishDate = () => {
    const spanishMonths = [
      'enero',
      'febrero',
      'marzo',
      'abril',
      'mayo',
      'junio',
      'julio',
      'agosto',
      'septiembre',
      'octubre',
      'noviembre',
      'diciembre',
    ];
    const now = new Date();
    return now.getDate() + ' de ' + spanishMonths[now.getMonth()] + ' de ' + now.getFullYear();
  };
  // Función para generar certificado
  const generateCertificate = async (course) => {
    let certificationData = Object.assign({}, coursesCertificationData);
    const key = course.contentProviderId + '_' + course.courseId + '_' + course['attr.type'];
    certificationData[key].buttonProps = {
      buttonLabel: 'Generando Certificado...',
      waiting: true,
    };
    setCoursesCertificationData(certificationData);
    try {
      const blob = await handleFetchPDF({
        url: 'certifications/gencertificates/download',
        method: 'POST',
        data: {
          certCode: course['attr.certificationTemplateId'],
          courseName: course?.title,
          userId: currUser.id,
          studyId: course?.courseId,
          certDate: getSpanishDate(),
          receiver: {
            fullname: `${currUser?.name} ${currUser?.lastname}`,
            document: currUser?.document,
            email: currUser?.email,
          },
        },
        responseType: 'blob',
      });

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'certificado.pdf';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      certificationData = Object.assign({}, coursesCertificationData);

      let aux = [];
      await handleFetch({
        url: 'courses/taken',
        method: 'GET',
        headers: { Authorization: `Bearer ${authToken}` },
      }).then(
        async (r) => {
          const cursosFiltrados = await r.data.filter((cor) => course?.courseId === cor?.slug);
          aux = cursosFiltrados[0] ? cursosFiltrados[0] : [];
        },
        (error) => {
          console.log(error.errors);
        }
      );
      certificationData[key].buttonProps = {
        buttonLabel: 'Descargar Certificado',
        waiting: false,
      };
      certificationData[key].certificateUrls = {
        certificationUrl: aux['user_study.users_studies_certification.certificationUrl'],
        certificationViewUrl: aux['user_study.users_studies_certification.certificationViewUrl'],
        certificationShareUrl: aux['user_study.users_studies_certification.certificationShareUrl'],
        certificationShareLinkedInUrl:
          aux['user_study.users_studies_certification.certificationShareLinkedInUrl'],
        certificationShareWhatsappUrl:
          aux['user_study.users_studies_certification.certificationShareWhatsappUrl'],
        type: 1,
      };
      certificationData[key].certificateData = {
        certificationId: aux['user_study.users_studies_certification.certificationId'],
        certificationIssueDate: new Date(),
      };
      setUrlCertificate(aux['user_study.users_studies_certification.certificationShareUrl']);
      setShowToast({
        show: true,
        type: 'success',
        message: `<span>Tu certificado para el curso <strong>${course.name}</strong> se generó exitosamente. Ya puedes descargarlo.</span>`,
      });
    } catch (error) {
      console.error('There was an error downloading the PDF:', error);
    }
    certificationData[key].buttonProps = {
      buttonLabel: 'Descargar Certificado',
      waiting: false,
      actionHandler: downloadCertificate,
    };
    setCoursesCertificationData(certificationData);
  };
  const [showNoPlanDialog, setShowNoPlanDialog] = useState({ show: false });

  // Función mostrar mensaje plan no tiene certificado
  const noPlanCertificate = (course) => {
    setShowNoPlanDialog({ show: true, courseName: course.name });
  };

  const closeNoPlanDialog = () => {
    setShowNoPlanDialog({ show: false });
  };
  // Función descargar certificado
  const downloadCertificate = async (course) => {
    try {
      const blob = await handleFetchPDF({
        url: 'certifications/gencertificates/download',
        method: 'POST',
        data: {
          certCode: 'XewdiQbAtoPT',
          courseName: course?.title,
          userId: currUser.id,
          studyId: course?.courseId,
          certDate: getSpanishDate(),
          receiver: {
            fullname: `${currUser?.name} ${currUser?.lastname}`,
            document: currUser?.document,
            email: currUser?.email,
          },
        },
        responseType: 'blob',
      });

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'certificado.pdf';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('There was an error downloading the PDF:', error);
    }
  };
  // Función generar certificado
  const getCertificationData = (courses) => {
    let certificationData = {};
    courses.map((c) => {
      const key = c.contentProviderId + '_' + c.courseId + '_' + c['attr.type'];

      certificationData[key] = {};
      certificationData[key].buttonProps =
        c['user_study.users_studies_certification.certificationId'] == null
          ? {
              buttonLabel: c['attr.type'] == 1 ? 'Generar Certificado' : 'Generar Insignia',
              wait: false,
              actionHandler: generateCertificate,
            }
          : {
              buttonLabel:
                c['user_study.users_studies_certification.certificationId'] == 1
                  ? 'Descargar Certificado'
                  : 'Descargar Insignia',
              wait: false,
              actionHandler: downloadCertificate,
            };
      certificationData[key].certificateUrls = {
        certificationUrl: c['user_study.users_studies_certification.certificationUrl'],
        certificationViewUrl: c['user_study.users_studies_certification.certificationViewUrl'],
        certificationShareUrl: c['user_study.users_studies_certification.certificationShareUrl'],
        certificationShareLinkedInUrl:
          c['user_study.users_studies_certification.certificationShareLinked'],
        certificationShareWhatsappUrl:
          c['user_study.users_studies_certification.certificationShareWhatsa'],
      };
      certificationData[key].certificateData = {
        certificationId: c['user_study.users_studies_certification.certificationId'],
        certificationIssueDate: c['user_study.users_studies_certification.certificationIssueDate'],
      };
    });
    return certificationData;
  };

  const [coursesCertificationData, setCoursesCertificationData] = useState(
    getCertificationData(coursesFiltered)
  );
  const handleChangePage = (event, page) => {
    setCoursesDisplayed(arrayPaginated(coursesFiltered, page));
    window.scrollTo(0, 0);
  };

  const toastReset = () => {
    setShowToast({ show: false });
  };

  const changeNoPlanHandler = (courses) => {
    let certificationData = Object.assign({}, coursesCertificationData);
    courses.map((c) => {
      const key = c.contentProviderId + '_' + c.courseId + '_' + c['attr.type'];
      if (c['user_study.users_studies_certification.certificationId'] == null) {
        certificationData[key].buttonProps.actionHandler = noPlanCertificate;
      }
    });
    return certificationData;
  };

  const [certificationNotAllowed, setCertificationNotAllowed] = useState(false);

  const changeNoCertificationsLeft = (courses) => {
    let certificationData = Object.assign({}, coursesCertificationData);
    courses.map((c) => {
      const key = c.contentProviderId + '_' + c.courseId + '_' + c['attr.type'];
      if (c['user_study.users_studies_certification.certificationId'] == null) {
        certificationData[key].buttonProps.disabled = true;
      }
    });
    return certificationData;
  };

  useEffect(() => {
    checkUserPlan(authToken).then((hasPlan) => {
      if (!hasPlan || (typeof hasPlan == 'string' && !JSON.parse(hasPlan))) {
        const certificationData = changeNoPlanHandler(coursesFiltered);
        setCoursesCertificationData(certificationData);
      } else if (!hasPlan.canCertificate) {
        setCertificationNotAllowed(true);
        const certificationData = changeNoCertificationsLeft(coursesFiltered);
        setCoursesCertificationData(certificationData);
      }
    });
  }, []);
  // Función mostrar certificados y/o mensajes de error
  const CertificateActions = ({ canCertificate, course, certificationData }) => {
    let firstButton, secondButton;

    if (
      canCertificate ||
      course['user_study.users_studies_certification.certificationId'] != null
    ) {
      firstButton = (
        <CourseActionButton {...certificationData.buttonProps} course={course}></CourseActionButton>
      );
      secondButton = (
        <CopyLinkToClipboard
          course={course}
          link={certificationData.certificateUrls.certificationViewUrl}
        ></CopyLinkToClipboard>
      );
    } else {
      firstButton = (
        <div
          className={`col-12 ProfilePage__download-certificate-button ProfilePage__download-certificate-button--disabled-green`}
        >
          <span>No disponible en tu Plan</span>
        </div>
      );

      secondButton = (
        <Link to="/planes" className={`col-12 ProfilePage__copy-link-button`}>
          <i className="fa-solid fa-credit-card"></i> <span className="ms-2">Ir a Planes</span>
        </Link>
      );
    }

    return (
      <>
        <div className="row" style={{ marginBottom: '5px' }}>
          <div className="ProfilePage__container-button">{firstButton}</div>
        </div>
        <div className="row">
          <div className="ProfilePage__container-button">{secondButton}</div>
        </div>
      </>
    );
  };
  return (
    <>
      <TakeCourseIndicators
        showWait={data.loading}
        showError={errorDialog}
        errorCloser={() => setErrorDialog(false)}
      />
      <div className="row">
        <div className="col-12">
          {coursesDisplayed.length === 0 && (
            <div className="col mx-sm-5">
              <div className="text-center text_primary my-5">
                <span className="fa-stack fa-3x">
                  <i className="fa-solid fa-graduation-cap fa-stack-1x"></i>
                  <i className="fa-solid fa-ban fa-stack-2x"></i>
                </span>
                <p style={{ fontSize: '20px' }}>No has completado ningún curso</p>
              </div>
            </div>
          )}
          {Object.keys(coursesCertificationData).length !== 0 &&
            coursesDisplayed.map((course) => {
              const certDataKey =
                course.contentProviderId + '_' + course.courseId + '_' + course['attr.type'];
              const enableShare =
                coursesCertificationData[certDataKey].certificateUrls.certificationUrl != null;

              const twitterMessage = `Estoy feliz porque terminé el curso ${course.name} en @EanX #EducaciónSinBarreras. No te quedes atrás, aprende y evoluciona (www.eanx.co)`;
              const whatsappMessage =
                `¡Cumplí una meta más! Me certifiqué en *${course.name}* con *${course.instructorData.name}*. EanX #EducaciónSinBarreras ` +
                coursesCertificationData[certDataKey].certificateUrls
                  .certificationShareWhatsappUrl +
                '.';
              const linkedinLink = `https://www.linkedin.com/profile/add?startTask=CERTIFICATION_NAME&name=${course.name}&organizationName=null&issueYear=${moment().format('YYYY')}&issueMonth=${moment().format('MM')}&expirationYear=Fecha inválida&expirationMonth=Fecha inválida&certUrl=${urlCertificate}&organizationId=58745`;

              const twitterLink = `https://twitter.com/intent/tweet/?text=${encodeURIComponent(twitterMessage)}`;
              const whatsappLink = `https://wa.me/?text=${encodeURIComponent(whatsappMessage)}`;

              return (
                <div
                  key={
                    course.contentProviderId + '__' + course.courseId + '__' + course['attr.type']
                  }
                  className="d-block d-md-flex ProfilePage__course-in-progress-row mb-5"
                >
                  <div className="d-flex justify-content-center me-md-3 align-items-center">
                    <img
                      className="ProfilePage__finished-course-image"
                      src={course.imageUrl}
                      onClick={() => {
                        onCourseRedirection(course);
                      }}
                    />
                  </div>
                  <div className="col-12 col-md-7 col-lg-5 col-xl-5">
                    <div className="row ProfilePage__container-course-title">
                      <span
                        className="ProfilePage__course-title"
                        onClick={() => {
                          onCourseRedirection(course);
                        }}
                      >
                        {course.title}
                      </span>
                    </div>
                    <CertificateActions
                      canCertificate={!certificationNotAllowed}
                      course={course}
                      certificationData={coursesCertificationData[certDataKey]}
                    ></CertificateActions>
                    {enableShare && (
                      <div className="row" style={{ marginTop: '8px' }}>
                        <div className="col-12 ProfilePage__share-buttons-container">
                          <span style={{ fontSize: '14px', marginRight: '5px' }}>Compártelo </span>
                          <ShareButton
                            enabled={enableShare}
                            link={
                              coursesCertificationData[certDataKey].certificateUrls
                                .certificationShareLinkedInUrl || linkedinLink
                            }
                            icon="linkedin"
                          ></ShareButton>
                          <ShareButton
                            enabled={enableShare}
                            link={twitterLink}
                            icon="twitter-square"
                          ></ShareButton>
                          <ShareButton
                            enabled={enableShare}
                            link={whatsappLink}
                            icon="whatsapp"
                          ></ShareButton>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
        </div>
      </div>
      {coursesDisplayed.length > 0 && (
        <div className="row mb-3">
          <div className="col-12 d-flex justify-content-center">
            <Pagination size="small" count={numberOfPages} onChange={handleChangePage} />
          </div>
        </div>
      )}
      <Toast
        show={showToast.show}
        message={showToast.message}
        type={showToast.type}
        closeCallback={toastReset}
      ></Toast>

      <Dialog
        open={showNoPlanDialog.show}
        classes={{ paper: 'AcceptDialog__container' }}
        disableScrollLock={true}
        onClose={closeNoPlanDialog}
      >
        <div>
          <div className="text-center ProfilePage__modal-icon ProfilePage__modal-icon--blue mt-2">
            <i className="fa-solid fa-award fa-3x"></i>
          </div>
          <div className="text-center px-3">
            <div className="ProfilePage__modal-title">Generar certificado</div>
            <div>
              Para poder generar el certificado del curso{' '}
              <strong>{showNoPlanDialog.courseName}</strong> debes estar suscrito a alguno de
              nuestros planes
            </div>
            <div className="ProfilePage__modal-buttons-container">
              <Link
                className="ProfilePage__modal-button ProfilePage__modal-button--highlight"
                to="/planes"
              >
                Ver Planes
              </Link>
              <button className="ProfilePage__modal-button" onClick={closeNoPlanDialog}>
                Cancelar
              </button>
            </div>
          </div>
        </div>
      </Dialog>
    </>
  );
};
